import { request, instanceMap, AvailableAxiosInstances } from "utils/request"

/**
 * Factory that creates a typed service object for a given axios instance.
 * Wraps utils/request.ts — no changes to request.ts required.
 *
 * Usage:
 *   const service = createService(SERVICE_INSTANCES.auth)
 *   service.get("/account/getusersession")
 *   service.post("/identity/authenticate", { username, password })
 *   service.upload("/admin/benefits/bulk_enroll_hmoplan", formData)
 */
export function createService(instanceKey: AvailableAxiosInstances) {
  return {
    instanceKey,

    get: <T = any>(url: string, params?: Record<string, any>, options?: { timeout?: number }) =>
      request.get({ url, params: params as any, timeout: options?.timeout }, instanceKey) as Promise<T>,

    post: <T = any>(
      url: string,
      body?: Record<string, any> | string | any,
      headers?: Record<string, string>
    ) => request.post({ url, body, headers }, instanceKey) as Promise<T>,

    patch: <T = any>(url: string, body?: Record<string, any>) =>
      request.patch({ url, body }, instanceKey) as Promise<T>,
    put: <T = any>(url: string, body?: Record<string, any>) =>
      request.put({ url, body }, instanceKey) as Promise<T>,

    delete: <T = any>(url: string) =>
      request.delete({ url }, instanceKey) as Promise<T>,

    /**
     * File download. Uses the raw axios instance because the response is a
     * binary body (spreadsheet / CSV) rather than the usual JSON envelope.
     */
    download: (url: string, params?: Record<string, any>): Promise<Blob> => {
      const instance = instanceMap[instanceKey]
      return instance
        .get(url, { params, responseType: "blob" })
        .then((res) => res.data)
    },

    /**
     * Multipart form-data upload. Uses the raw axios instance so the
     * Content-Type boundary is set automatically by axios.
     */
    upload: <T = any>(
      url: string,
      formData: FormData,
      params?: Record<string, any>
    ): Promise<T> => {
      const instance = instanceMap[instanceKey]
      return instance.post(url, formData, { params }).then((res) => res.data)
    },
  }
}
